import React from "react";
import { mediaUrl } from "@/lib/admin/api";

/* ============================================================
   Shared admin primitives for the 2High Operator Hub
   - useAutoSave: debounce + saved-tick UX
   - SavedIndicator: status chip shown next to each form
   - MediaSlot: click-to-replace image or video slot with drag-drop
   - SoftLabel / Field / Row: compact form building blocks
   ============================================================ */

export const SAVED_STATES = { idle: "idle", dirty: "dirty", saving: "saving", saved: "saved", error: "error" };

// Debounced auto-save. Returns { state, push, flush, reset }.
export function useAutoSave(saveFn, delay = 650) {
  const [state, setState] = React.useState(SAVED_STATES.idle);
  const tRef = React.useRef(null);
  const pendingRef = React.useRef(null);

  const flush = React.useCallback(async () => {
    clearTimeout(tRef.current);
    tRef.current = null;
    const payload = pendingRef.current;
    if (payload == null) return;
    pendingRef.current = null;
    setState(SAVED_STATES.saving);
    try {
      await saveFn(payload);
      setState(SAVED_STATES.saved);
      setTimeout(() => setState((s) => (s === SAVED_STATES.saved ? SAVED_STATES.idle : s)), 1800);
    } catch (e) {
      console.error("auto-save failed", e);
      setState(SAVED_STATES.error);
    }
  }, [saveFn]);

  const push = React.useCallback(
    (payload) => {
      pendingRef.current = payload;
      setState(SAVED_STATES.dirty);
      clearTimeout(tRef.current);
      tRef.current = setTimeout(flush, delay);
    },
    [flush, delay]
  );

  React.useEffect(() => () => clearTimeout(tRef.current), []);
  return { state, push, flush, reset: () => setState(SAVED_STATES.idle) };
}

export function SavedIndicator({ state, className = "" }) {
  const map = {
    [SAVED_STATES.idle]: { t: "", c: "text-[#e8e4d8]/30" },
    [SAVED_STATES.dirty]: { t: "Editing…", c: "text-[#c9b88a]/70" },
    [SAVED_STATES.saving]: { t: "Saving…", c: "text-[#fbbf24]" },
    [SAVED_STATES.saved]: { t: "Saved ✓", c: "text-[#6ee7b7]" },
    [SAVED_STATES.error]: { t: "Save failed", c: "text-red-300" },
  };
  const { t, c } = map[state] || map.idle;
  if (!t) return <span className={`text-[10px] tracking-[.28em] uppercase ${c} ${className}`}>&nbsp;</span>;
  return (
    <span data-testid="saved-indicator" className={`inline-flex items-center gap-1.5 text-[10px] tracking-[.28em] uppercase ${c} ${className}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current" />{t}
    </span>
  );
}

// Floating toast queue
const toastListeners = new Set();
export function toast(msg, kind = "ok") {
  toastListeners.forEach((fn) => fn({ id: Math.random(), msg, kind }));
}
export function ToastHost() {
  const [items, setItems] = React.useState([]);
  React.useEffect(() => {
    const fn = (t) => {
      setItems((xs) => [...xs, t]);
      setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== t.id)), 2400);
    };
    toastListeners.add(fn);
    return () => toastListeners.delete(fn);
  }, []);
  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[200] flex flex-col gap-2 items-center pointer-events-none">
      {items.map((t) => (
        <div key={t.id} data-testid="toast" className={`pointer-events-auto px-4 py-2 rounded-full text-[11px] tracking-[.28em] uppercase font-semibold shadow-lg ${
          t.kind === "error" ? "bg-red-500/95 text-white" : t.kind === "warn" ? "bg-[#fbbf24]/95 text-[#04060f]" : "bg-[#6ee7b7]/95 text-[#04060f]"
        }`}>{t.msg}</div>
      ))}
    </div>
  );
}

// Detect if a URL is a video by extension or by /api/media/original/* path
export function isVideoUrl(url) {
  if (!url) return false;
  const u = String(url).toLowerCase();
  return /\.(mp4|webm|mov|m4v|ogg|ogv)(\?|$)/.test(u) || /\/api\/media\/original\//.test(u) || /\/api\/media\/file\/[^/]+-web\.mp4$/.test(u);
}

// Drag-and-drop media slot. Supports image OR video. Accepts an external URL via paste.
export function MediaSlot({ label, value, onChange, accept = "any", uploadFn, poster = "", className = "", testid = "" }) {
  const [progress, setProgress] = React.useState(null); // null | 0-100
  const [errorMsg, setErrorMsg] = React.useState("");
  const [drag, setDrag] = React.useState(false);
  const inputRef = React.useRef(null);
  const acceptAttr = accept === "image" ? "image/*" : accept === "video" ? "video/*" : "image/*,video/*";

  async function handleFile(f) {
    if (!f) return;
    // Client-side size sanity check — 200MB hard cap to avoid hanging uploads
    if (f.size > 200 * 1024 * 1024) {
      setErrorMsg("File too large (max 200 MB). Please compress before uploading.");
      toast("File too large — max 200 MB", "error");
      return;
    }
    setErrorMsg("");
    setProgress(0);
    try {
      const meta = await uploadFn(f, (pct) => setProgress(pct));
      const url = meta.web_url || meta.url || meta.url_original;
      onChange(url, meta.poster_url || poster);
      setProgress(100);
      setTimeout(() => setProgress(null), 600);
    } catch (e) {
      setProgress(null);
      const msg = e.response?.data?.detail || e.message || "Upload failed";
      setErrorMsg(msg);
      toast(msg, "error");
    }
  }

  const isVideo = isVideoUrl(value);
  const thumb = value ? (isVideo ? (poster ? mediaUrl(poster) : null) : mediaUrl(value)) : null;
  const uploading = progress !== null && progress < 100;
  const justDone = progress === 100;

  return (
    <div className={`relative ${className}`}>
      {label && <div className="text-[10px] tracking-[.32em] uppercase text-[#c9b88a]/70 mb-1.5">{label}</div>}
      <div
        data-testid={testid}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={async (e) => { e.preventDefault(); setDrag(false); const f = e.dataTransfer.files[0]; await handleFile(f); }}
        className={`relative aspect-[4/3] w-full rounded-xl overflow-hidden border-2 border-dashed transition-all ${
          drag ? "border-[#c9b88a] bg-[#c9b88a]/8" : "border-[#c9b88a]/25 bg-white/[.02]"
        }`}
      >
        {thumb ? <img src={thumb} alt="" className="w-full h-full object-cover" /> : null}
        {isVideo && !thumb ? (
          <video src={mediaUrl(value)} muted preload="metadata" className="w-full h-full object-cover" />
        ) : null}
        {isVideo ? (
          <span className="absolute top-2 right-2 px-2 py-0.5 rounded text-[9px] tracking-[.28em] uppercase bg-[#04060f]/80 text-[#6ee7b7] border border-[#6ee7b7]/50">Video</span>
        ) : null}

        {/* Empty state */}
        {!value && !uploading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-4">
            <button
              data-testid={testid ? `${testid}-add` : undefined}
              onClick={() => inputRef.current?.click()}
              className="px-4 py-2 rounded-full bg-[#c9b88a] text-[#04060f] text-[10px] tracking-[.28em] uppercase font-bold hover:bg-white transition"
            >+ Add Media</button>
            <span className="text-[9px] tracking-[.28em] uppercase text-[#e8e4d8]/35">
              {accept === "image" ? "JPG · PNG · WEBP" : accept === "video" ? "MP4 · MOV · WEBM" : "Image or Video"} · max 200 MB
            </span>
            <span className="text-[9px] text-[#e8e4d8]/25">or drag &amp; drop here</span>
          </div>
        )}

        {/* Upload progress overlay */}
        {uploading && (
          <div className="absolute inset-0 bg-[#04060f]/85 flex flex-col items-center justify-center gap-3">
            <div className="w-4/5 max-w-[200px] h-1.5 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full bg-[#c9b88a] transition-all" style={{ width: `${progress}%` }} data-testid={testid ? `${testid}-progress` : undefined} />
            </div>
            <span className="text-[11px] tracking-[.28em] uppercase text-[#fbbf24]">Uploading {progress}%</span>
          </div>
        )}
        {justDone && !value && (
          <div className="absolute inset-0 bg-[#04060f]/85 flex items-center justify-center text-[11px] tracking-[.28em] uppercase text-[#6ee7b7]">Processing…</div>
        )}

        {/* Always-visible delete X on populated slot — works on touch */}
        {value && !uploading && (
          <button
            type="button"
            data-testid={testid ? `${testid}-clear` : undefined}
            onClick={(e) => {
              e.stopPropagation();
              if (window.confirm("Remove this media?")) onChange("", "");
            }}
            title="Remove this media"
            className="absolute top-2 right-2 z-10 w-8 h-8 rounded-full grid place-items-center text-[14px] font-bold bg-red-500/95 text-white shadow-lg hover:bg-red-600 transition border-2 border-white/40"
          >×</button>
        )}

        {/* Replace button on hover (desktop refinement) */}
        {value && !uploading && (
          <div className="absolute inset-0 bg-gradient-to-t from-[#04060f]/80 via-transparent to-transparent flex items-end justify-center p-2 opacity-0 hover:opacity-100 transition pointer-events-none">
            <button
              onClick={() => inputRef.current?.click()}
              className="text-[9px] tracking-[.28em] uppercase px-3 py-1.5 rounded-full bg-[#c9b88a] text-[#04060f] font-semibold pointer-events-auto"
            >Replace</button>
          </div>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={acceptAttr}
        className="hidden"
        onChange={async (e) => { const f = e.target.files?.[0]; if (f) await handleFile(f); e.target.value = ""; }}
      />

      {errorMsg && (
        <div data-testid={testid ? `${testid}-error` : undefined} className="mt-2 text-[11px] text-red-300 bg-red-500/10 border border-red-500/25 rounded-md px-3 py-2">{errorMsg}</div>
      )}

      <div className="mt-1.5">
        <input
          type="url"
          placeholder="Or paste URL (https:// or YouTube)"
          value={value || ""}
          onChange={(e) => onChange(e.target.value, poster)}
          className="w-full px-2.5 py-1.5 text-[11px] rounded-md bg-white/[.03] border border-[#c9b88a]/15 text-[#e8e4d8]/80 focus:outline-none focus:border-[#c9b88a]/50"
        />
      </div>
    </div>
  );
}

export const Field = ({ label, children, hint }) => (
  <label className="block">
    {label && <span className="block text-[10px] tracking-[.32em] uppercase text-[#c9b88a]/70 mb-1.5">{label}</span>}
    {children}
    {hint && <span className="block text-[10px] text-[#e8e4d8]/40 mt-1">{hint}</span>}
  </label>
);

export const TextInput = React.forwardRef(function TextInput({ className = "", ...p }, ref) {
  return (
    <input
      ref={ref}
      {...p}
      className={`w-full px-3 py-2.5 rounded-lg bg-white/[.04] border border-[#c9b88a]/20 text-[#e8e4d8] text-sm focus:outline-none focus:border-[#c9b88a]/60 focus:bg-[#c9b88a]/[.04] transition ${className}`}
    />
  );
});

export const TextArea = ({ className = "", ...p }) => (
  <textarea
    {...p}
    className={`w-full px-3 py-2.5 rounded-lg bg-white/[.04] border border-[#c9b88a]/20 text-[#e8e4d8] text-sm focus:outline-none focus:border-[#c9b88a]/60 resize-y min-h-[80px] ${className}`}
  />
);

export const SelectInput = ({ options = [], className = "", ...p }) => (
  <select
    {...p}
    className={`w-full px-3 py-2.5 rounded-lg bg-white/[.04] border border-[#c9b88a]/20 text-[#e8e4d8] text-sm focus:outline-none focus:border-[#c9b88a]/60 ${className}`}
  >
    {options.map((o) => (
      <option key={o.value} value={o.value} className="bg-[#04060f]">{o.label}</option>
    ))}
  </select>
);

export const Toggle = ({ checked, onChange, label, testid }) => (
  <label className="flex items-center gap-3 cursor-pointer select-none">
    <span className={`relative w-10 h-5 rounded-full transition-colors ${checked ? "bg-[#c9b88a]" : "bg-white/10"}`}>
      <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${checked ? "translate-x-5" : ""}`} />
    </span>
    <input data-testid={testid} type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} className="hidden" />
    {label && <span className="text-[11px] tracking-[.22em] uppercase text-[#e8e4d8]/80">{label}</span>}
  </label>
);

export const Pill = ({ active, onClick, children, color = "accent", testid }) => {
  const cls = active
    ? (color === "danger" ? "bg-red-400 text-[#04060f] border-red-400" : color === "warn" ? "bg-[#fbbf24] text-[#04060f] border-[#fbbf24]" : color === "ok" ? "bg-[#6ee7b7] text-[#04060f] border-[#6ee7b7]" : "bg-[#c9b88a] text-[#04060f] border-[#c9b88a]")
    : "bg-white/5 text-[#e8e4d8]/70 border-[#c9b88a]/20 hover:bg-white/10";
  return (
    <button data-testid={testid} onClick={onClick} className={`px-2.5 py-1 rounded-full text-[9.5px] tracking-[.25em] uppercase border font-medium transition ${cls}`}>{children}</button>
  );
};
